import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { useCheckEligibleProductsMutation } from '@redux/queries/claimRecords'
import {
  setEligibleProductCheckError,
  setEligibleProductCheckInProgress,
  setEligibleProductCheckResult,
} from '@redux/slices/claimStatusSlice'
import { useCart } from '@hooks/useCart'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { eligibleProductController } from '@hooks/useEligibleProductCheck/eligibleProductCheckController'

export const useCartBundleChangeEventHandler = (): (() => void) => {
  const dispatch = useDispatch()
  const { fetchCartBundle } = useCart()
  const { claimRecordId, fetchClaimRecord } = useClaimRecord()
  const [triggerCheck] = useCheckEligibleProductsMutation()

  const { isActiveDataChecked, isEligibleProductCheckInProgress } = useSessionInfo()

  const performCheck = useCallback(async () => {
    const service = {
      fetchClaimRecord,
      fetchCartBundle,
      fetchEligibleProducts: (products: ProductBundle[]) => triggerCheck(products).unwrap(),

      onComplete: async (status: EligibleProductStatus) => {
        dispatch(setEligibleProductCheckResult(status))
      },
      onError: async (error?: unknown) => {
        dispatch(setEligibleProductCheckError())
      },
    }

    const controller = eligibleProductController(service, claimRecordId)
    await controller.checkEligibility()
  }, [dispatch, triggerCheck, fetchClaimRecord, fetchCartBundle, claimRecordId])

  return useCallback(() => {
    if (!isActiveDataChecked || isEligibleProductCheckInProgress) return

    dispatch(setEligibleProductCheckInProgress(true))
    performCheck()
      .catch(console.warn)
      .finally(() => dispatch(setEligibleProductCheckInProgress(false)))
  }, [dispatch, performCheck, isActiveDataChecked, isEligibleProductCheckInProgress])
}
